import type { AllFieldsMessage, FieldCardType, FieldMessage, FieldName } from 'netvalve-types';

// an event is triggered when the field is validated
export function handleValidation(
    message: FieldMessage,
    tokenizedFields: Set<FieldName>,
    setTokenizedFields: (fieldToAdd: FieldName | null, fieldToRemove?: FieldName) => void,
    callback?: ((message: FieldMessage) => void) | null
) {
    window.dispatchEvent(new CustomEvent('field-validation', { detail: message }));
    if (callback) callback(message);
    if (!message.success && tokenizedFields.size === 3) {
        // if the field is not successful and the number of tokenized fields is 3, dispatch the failed all-fields-tokenized event
        const allFieldsMessage = {
            ...message,
            type: 'tokenization',
            success: false,
            fieldName: 'allFields',
        } as AllFieldsMessage;
        window.dispatchEvent(new CustomEvent('all-fields-tokenized', { detail: allFieldsMessage }));
    }
    if (!message.success) setTokenizedFields(null, message.fieldName); // the field is not validated, remove it from the list
}

// an event is triggered when the field is tokenized
export function handleTokenization(
    message: FieldMessage,
    tokenizedFields: Set<FieldName>,
    setTokenizedFields: (fieldToAdd: FieldName | null, fieldToRemove?: FieldName) => void,
    updatePendingEncryptions: (field: FieldName) => void,
    callback?: ((message: FieldMessage) => void) | null
) {
    window.dispatchEvent(new CustomEvent('field-tokenization', { detail: message }));
    if (callback) callback(message);
    if (!message.success)
        setTokenizedFields(null, message.fieldName); // if fails, remove from the list
    else setTokenizedFields(message.fieldName); // if success, add to the list
    updatePendingEncryptions(message.fieldName); // remove from pending encryptions, whether success or not
    if (tokenizedFields.size === 3) {
        // if after updating the tokenized fields, the size is 3, dispatch the successful all-fields-tokenized event
        const allFieldsMessage = { ...message, success: true, fieldName: 'allFields' } as AllFieldsMessage;
        window.dispatchEvent(new CustomEvent('all-fields-tokenized', { detail: allFieldsMessage }));
    }
}

// an event is triggered when the field is initialized
// if all fields are initialized, the all-fields-ready event is triggered. fields-ready is a legacy event.
export function handleInitiated(message: FieldMessage, initializedFields: Set<FieldName>) {
    window.dispatchEvent(new CustomEvent('field-initiated', { detail: message }));
    initializedFields.add(message.fieldName);
    if (initializedFields.size === 3) {
        window.dispatchEvent(new CustomEvent('fields-ready', { detail: Array.from(initializedFields) }));
        const allFieldsMessage = { ...message, success: true, fieldName: 'allFields' } as AllFieldsMessage;
        window.dispatchEvent(new CustomEvent('all-fields-ready', { detail: allFieldsMessage }));
    }
}

export function handleCardType(
    message: FieldMessage,
    cardType: FieldCardType | null,
    setCardType: (cardType: FieldCardType) => void
) {
    setCardType(message.typeData!);
    if (message.typeData?.type !== cardType?.type) {
        setCardType(message.typeData!);

        // Dispatch this event for merchants
        window.dispatchEvent(
            new CustomEvent('card-type', {
                detail: message,
            })
        );
    }
}
